import AutocompleteStyled from "@/components/AutocompleteStyled";
import { ModalStyled } from "@/components/ModalStyled";
import { createVenue, useLocations } from "@/lib/api";
import { basierCirleRegular } from "@/styles/fonts";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Button, Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const ModalAddVenue = dynamic(() => import("./bandedit/ModalAddVenue"));

export const LocationSelector = ({
  name,
  zipcodeId,
  defaultValue,
  onChange,
  label,
  placeholder,
  hint,
  required = false,
  disabeld = false,
}) => {
  const { locations, isLoading, isError, mutate } = useLocations([
    process.env.NEXT_PUBLIC_LOCATIONCATEGORYID_VENUES,
  ]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(defaultValue);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpen = () => setIsModalOpen(true);
  const handleModalClosed = () => setIsModalOpen(false);

  useEffect(() => {
    if (!locations) {
      setFilteredLocations([]);
      return;
    }
    if (!zipcodeId) {
      setFilteredLocations(locations);
      return;
    }

    const newLocations = locations.filter(
      (l) => l.zipcode?.id + "" === zipcodeId + "",
    );
    setFilteredLocations(newLocations);

    if (selectedLocation) {
      if (!newLocations.find((nl) => nl.id + "" === selectedLocation.id + "")) {
        setSelectedLocation(null);

        if (onChange) {
          onChange({ target: { name, value: null } }, null);
        }
      }
    }
  }, [locations, zipcodeId]);

  const handleSelected = (event, value) => {
    setSelectedLocation(value);

    if (onChange) {
      onChange({ target: { name, value: value } }, value);
    }
  };

  const handleAddLocation = async (value) => {
    const newLocation = {
      category: process.env.NEXT_PUBLIC_LOCATIONCATEGORYID_VENUES,
      name: value,
      zipcode: zipcodeId,
      source: "user",
    };

    const createdLocation = await createVenue(newLocation);
    await mutate();
    setFilteredLocations([...filteredLocations, createdLocation]);

    setSelectedLocation(createdLocation);
    setIsModalOpen(false);
    if (onChange) {
      onChange(
        { target: { name, value: createdLocation.id } },
        createdLocation,
      );
    }
  };

  if (isLoading) {
    return <Skeleton />;
  }

  const style = {
    "& .wrapper": {
      padding: "0.75rem 1rem 0.25rem 1rem",
      "& .MuiButton-root": {
        borderRadius: "0.25rem",
        height: "2rem",
        fontWeight: basierCirleRegular.style.fontWeight,
        fontFamily: basierCirleRegular.style.fontFamily,
      },
    },
  };

  const PaperComponent = (options) => {
    const { props, ref, containerProps, children } = options;

    return (
      <Paper {...containerProps} sx={style}>
        {locations?.length === 0 ? (
          <></>
        ) : (
          <>
            <Box className="wrapper">
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<AddBoxIcon />}
                onMouseDown={handleOpen}
              >
                Neue Venue erstellen
              </Button>
            </Box>
            <Fade in>
              <Box ref={ref} {...props}>
                {children}
              </Box>
            </Fade>
          </>
        )}
      </Paper>
    );
  };

  return (
    <>
      <AutocompleteStyled
        label={label}
        hint={hint}
        options={filteredLocations}
        placeholder={placeholder}
        required={required}
        onChange={handleSelected}
        value={selectedLocation ?? defaultValue}
        getOptionLabel={(option) => option.name}
        PaperComponent={PaperComponent}
        disabled={disabeld}
      />
      <ModalStyled open={isModalOpen} onClose={handleModalClosed}>
        <ModalAddVenue
          locations={filteredLocations}
          onChange={handleAddLocation}
        />
      </ModalStyled>
    </>
  );
};
